import {
  GeneratedSkedApiClient,
  GeneratedSkedApiError,
} from "../generated-client";
import type { paths } from "../generated/sked-api-openapi-types";
import {
  logUploadBreadcrumb,
  logUploadFailure,
  withUploadSpan,
} from "./upload-observability";
import { resolveMediaCorrelationKey } from "utils/media-observability";

const generatedSkedApiClient = new GeneratedSkedApiClient<paths>();
const DEFAULT_TRANSCODE_POLL_INTERVAL_MS = 1_000;
const MEDIACONVERT_POLL_INTERVAL_MS = 5_000;
const TRANSIENT_STATUS_RETRY_DELAYS_MS = [5_000, 10_000, 20_000];

export async function transcodeVideoByUrl(
  url: string,
  {
    onProgress,
    uploadAttemptId,
    mediaCorrelationKey,
    fileName,
  }: {
    onProgress(progress: number): void;
    uploadAttemptId: string;
    mediaCorrelationKey?: string;
    fileName?: string;
  }
): Promise<string> {
  const metadata = {
    url,
    uploadAttemptId,
    fileName,
    mediaCorrelationKey:
      mediaCorrelationKey ??
      resolveMediaCorrelationKey({ uploadAttemptId, urls: [url] }),
  };

  logUploadBreadcrumb("media_upload.transcode.start", metadata);

  return withUploadSpan("media_upload.transcode", metadata, async () => {
    try {
      const transcodeJob = await generatedSkedApiClient.unwrap(
        await generatedSkedApiClient.client.POST(
          "/content-inbox/transcode/start",
          {
            body: {
              url,
              observability: {
                uploadAttemptId,
                mediaCorrelationKey: metadata.mediaCorrelationKey,
                fileName,
              },
            },
          }
        )
      );
      const jobId = transcodeJob.id;

      if (jobId === undefined) {
        throw new Error("Transcoding job response did not include an id");
      }

      logUploadBreadcrumb("media_upload.transcode.job_created", {
        ...metadata,
        jobId,
      });
      const pollIntervalMs =
        transcodeJob.provider === "mediaconvert"
          ? MEDIACONVERT_POLL_INTERVAL_MS
          : DEFAULT_TRANSCODE_POLL_INTERVAL_MS;

      let transcode;
      let lastLoggedProgressBucket = -1;
      do {
        await wait(pollIntervalMs);
        transcode = await getJobStatusWithRetry(jobId);
        const currentBucket = Math.floor(transcode.progress / 10);
        if (currentBucket > lastLoggedProgressBucket) {
          logUploadBreadcrumb("media_upload.transcode.progress", {
            ...metadata,
            jobId,
            progress: transcode.progress,
            status: transcode.status,
          });
          lastLoggedProgressBucket = currentBucket;
        }
        onProgress(transcode.progress);
      } while (transcode.status === "processing");

      if (transcode.status !== "completed") {
        throw new Error(`Transcoding failed with status: ${transcode.status}`);
      }

      logUploadBreadcrumb("media_upload.transcode.complete", {
        ...metadata,
        jobId,
        progress: transcode.progress,
        status: transcode.status,
      });

      return jobId;
    } catch (error) {
      logUploadFailure("media_upload.transcode.failure", error, metadata);
      throw error;
    }
  });
}

async function getJobStatusWithRetry(jobId: string): Promise<JobStatus> {
  let retry = 0;
  while (true) {
    try {
      return await getJobStatus(jobId);
    } catch (error) {
      const delay = TRANSIENT_STATUS_RETRY_DELAYS_MS[retry];
      if (!isTransientStatusError(error) || delay === undefined) throw error;
      retry += 1;
      await wait(delay);
    }
  }
}

function isTransientStatusError(error: unknown): boolean {
  return (
    error instanceof GeneratedSkedApiError &&
    (error.status === 429 || error.status === 503)
  );
}

async function getJobStatus(jobId: string): Promise<JobStatus> {
  const data = await generatedSkedApiClient.unwrap(
    await generatedSkedApiClient.client.GET(
      "/content-inbox/transcode/{jobId}",
      {
        params: {
          path: { jobId: String(jobId) },
        },
      }
    )
  );
  const outputUrls = (data.output_urls ?? {}) as TranscodeOutputUrls;

  const urlKey = Object.keys(outputUrls).find((key) => key.startsWith("mp4"));
  const thumbnailKey = Object.keys(outputUrls).find(
    (key) => key.startsWith("jpg") && key.endsWith("#start")
  );

  return {
    status: data.status ?? "",
    progress: Number.parseInt(data.progress ?? "0", 10),
    url: (urlKey && getFirstOutputUrl(outputUrls[urlKey])) ?? "",
    thumbnail:
      (thumbnailKey && getFirstOutputUrl(outputUrls[thumbnailKey])) ?? "",
  };
}

function getFirstOutputUrl(value: TranscodeOutputUrls[string]): string {
  if (Array.isArray(value)) {
    return value[0] ?? "";
  }

  return value ?? "";
}

async function wait(ms: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

interface JobStatus {
  progress: number;
  status: string;
  url: string;
  thumbnail: string;
}

type TranscodeOutputUrls = Record<string, string | string[] | undefined>;
